import type { Station } from '@/content/schema';
import type { ComponentType } from 'react';
import type { HeroData, PropsElement } from '@/hero/data';
import type { LayerHandle } from '@/hero/GlbLayer';
import type { PickMode, Selection } from '@/state/selection';

export interface Frame {
  station: Station;
  t: number;
  progress: number;
  dt: number;
  // the GLB layers plus, once the visitor built something, the store layer under 'store'
  layers: Map<string, LayerHandle>;
  height: number;
  data: HeroData | null;
}

// enter/update/leave drive the layer rigs every frame, Panel is the DOM card on the right,
// Scene adds r3f children, Card extends the element card on click.
export interface StationImpl {
  pick: PickMode;
  enter?(frame: Frame): void;
  update(frame: Frame): void;
  leave?(frame: Frame): void;
  Panel?: ComponentType;
  Scene?: ComponentType;
  Card?: ComponentType<{ selection: Selection; element: PropsElement | undefined }>;
  ownsSelection?: boolean;
}

export function resetLayers(layers: Map<string, LayerHandle>, height: number): void {
  for (const h of layers.values()) {
    h.rig.clearStates();
    h.rig.setGhost(0);
    h.rig.setReveal(2);
    h.rig.setClipY(height + 2);
    h.rig.setVisible(h.model.discipline !== 'space');
  }
}

const TYPE_RANK: Record<string, number> = {
  IFCFOOTING: 0,
  IFCSLAB: 1,
  IFCWALL: 2,
  IFCWALLSTANDARDCASE: 2,
  IFCCOLUMN: 3,
  IFCBEAM: 3,
  IFCMEMBER: 3,
  IFCSTAIRFLIGHT: 4,
  IFCSTAIR: 4,
  IFCROOF: 5,
  IFCWINDOW: 6,
  IFCDOOR: 6,
  IFCRAILING: 7,
  IFCCOVERING: 8,
  IFCFURNISHINGELEMENT: 9,
};

// storey by storey, structure before openings before finishes, MEP after the shell
export function elementsInBuildOrder(
  layers: LayerHandle[],
): { layer: LayerHandle; expressID: number; storeyIndex: number; type: string }[] {
  const out: { layer: LayerHandle; expressID: number; storeyIndex: number; type: string }[] = [];
  for (const layer of layers) {
    const mep = layer.model.discipline === 'mep';
    for (const el of layer.rig.elements.values()) {
      out.push({ layer, expressID: el.expressID, storeyIndex: el.storeyIndex, type: el.type });
      if (mep) out[out.length - 1]!.type = 'ZMEP';
    }
  }
  const rank = (t: string) => (t === 'ZMEP' ? 10 : (TYPE_RANK[t.toUpperCase()] ?? 8));
  out.sort(
    (a, b) =>
      a.storeyIndex - b.storeyIndex || rank(a.type) - rank(b.type) || a.expressID - b.expressID,
  );
  return out;
}

export function layersOf(
  layers: Map<string, LayerHandle>,
  test: (discipline: string) => boolean,
): LayerHandle[] {
  return [...layers.values()].filter((h) => test(h.model.discipline));
}
